import { action, computed, toJS } from 'mobx';
import { Entity } from '../../core/entity';
import type { IObjectViewModel, SceneItemKind } from '../interfaces';
import type { Transparency } from '../interfaces';
import type { ImagesMode, SliceType } from '../modules/images/interfaces';

export interface ObjectViewModelProps {
  descriptor: string;
  attachments: Array<string>;
  details: unknown;
  label: string;
  color: string;
  isVertexColoring: boolean;
  syncSliceDescriptor?: SliceType | undefined;
  highlightColor: string;
  selectionColor: string;
  transparency: Transparency;
  isVisible: boolean;
  isEnabled?: boolean;
  isContourVisible: boolean;
  isHighlighted: boolean;
  isSelected: boolean;
  isPickable: boolean;
  isTunable: boolean;
  isBeingEdited: boolean;
  isClippable: boolean;
  kind: SceneItemKind;
  zoomFactor?: number;
  selectedIndex?: number | undefined;
  imported: boolean;
  isAvailable: boolean;
  phaseId: string | undefined;
  surgeryExtension?: unknown;
  imagesMode?: ImagesMode;
  imagesOverlayColor?: string;
  isFluoroOverlay?: boolean;
  isLabelColored?: boolean;
}
export class BasicObjectViewModel extends Entity<ObjectViewModelProps> implements IObjectViewModel {
  public get descriptor(): string {
    return this.props.descriptor;
  }

  public get attachments(): Array<string> {
    return this.props.attachments;
  }

  @computed
  public get transparency(): Transparency {
    return this.props.transparency;
  }

  @computed
  public get label(): string {
    return this.props.label;
  }

  @computed
  public get color(): string {
    return this.props.color;
  }

  @computed
  public get isVertexColoring(): boolean {
    return this.props.isVertexColoring;
  }

  @computed
  public get isLabelColored(): boolean | undefined {
    return this.props.isLabelColored;
  }

  @computed
  public get phaseId(): string | undefined {
    return this.props.phaseId;
  }

  @computed
  public get highlightColor(): string {
    return this.props.highlightColor;
  }

  @computed
  public get selectionColor(): string {
    return this.props.selectionColor;
  }

  public get syncSliceDescriptor(): SliceType | undefined {
    return this.props.syncSliceDescriptor;
  }

  @computed
  public get selectedIndex(): number | undefined {
    return this.props.selectedIndex;
  }

  @computed
  public get imagesMode(): ImagesMode | undefined {
    return this.props.imagesMode;
  }

  @computed
  public get isVisible(): boolean {
    return this.props.isVisible && this.props.isAvailable;
  }

  @computed
  public get isAvailable(): boolean {
    return this.props.isAvailable;
  }

  @computed
  public get isClippable(): boolean {
    return this.props.isClippable;
  }

  @computed
  public get isContourVisible(): boolean {
    return this.props.isContourVisible;
  }

  @computed
  public get isHighlighted(): boolean {
    return this.props.isHighlighted;
  }

  @computed
  public get isSelected(): boolean {
    return this.props.isSelected;
  }

  @computed
  public get isPickable(): boolean {
    return this.props.isPickable;
  }

  @computed
  public get isTunable(): boolean {
    return this.props.isTunable;
  }

  @computed
  public get isBeingEdited(): boolean {
    return this.props.isBeingEdited;
  }

  @computed
  public get kind(): SceneItemKind {
    return this.props.kind;
  }

  @computed
  public get zoomFactor(): number {
    return this.props.zoomFactor ?? 0.5;
  }

  @computed
  public get details(): unknown {
    return this.props.details;
  }

  @computed
  public get imported(): boolean {
    return this.props.imported;
  }

  @computed
  public get surgeryExtension(): unknown {
    return this.props.surgeryExtension;
  }

  @computed
  public get imagesOverlayColor(): string | undefined {
    return this.props.imagesOverlayColor ?? undefined;
  }

  @computed
  public get isFluoroOverlay(): boolean {
    return this.props.isFluoroOverlay ?? false;
  }

  @computed
  public get isEnabled(): boolean {
    return this.props.isEnabled ?? true;
  }

  @action
  public setVisibility(visibility: boolean): void {
    this.props.isVisible = visibility;
  }

  @action
  public setAvailability(availability: boolean): void {
    this.props.isAvailable = availability;
  }

  @action
  public setContourVisibility(visibility: boolean): void {
    this.props.isContourVisible = visibility;
  }

  @action
  public setHighlighting(highlighting: boolean): void {
    this.props.isHighlighted = highlighting;
  }

  @action
  public setSelection(selected: boolean): void {
    this.props.isSelected = selected;
  }

  @action
  public setBeingEdited(isBeingEdited: boolean): void {
    this.props.isBeingEdited = isBeingEdited;
  }

  @action
  public setSelectedIndex(index: number | undefined): void {
    this.props.selectedIndex = index;
  }

  @action
  public setLabel(label: string): void {
    this.props.label = label;
  }

  @action
  public setDetails(details: unknown): void {
    this.props.details = details;
  }

  @action
  public setIsPickable(isPickable: boolean): void {
    this.props.isPickable = isPickable;
  }

  @action
  public setIsTunable(isTunable: boolean): void {
    this.props.isTunable = isTunable;
  }

  @action
  public setTransparency(transparency: Transparency): void {
    this.props.transparency = transparency;
  }

  @action
  public setColor(color: string): void {
    this.props.color = color;
  }

  @action
  public setHighlightColor(color: string): void {
    this.props.highlightColor = color;
  }

  @action
  public setSelectionColor(color: string): void {
    this.props.selectionColor = color;
  }

  @action
  public setIsEnabled(isEnabled: boolean): void {
    this.props.isEnabled = isEnabled;
  }

  public clone(): BasicObjectViewModel {
    return new BasicObjectViewModel(toJS(this.props));
  }
}
